// web/src/main.ts

import { version } from "../../package.json";
import { deserializeAST, serializeAST } from "../../src/spd/ast";
import { ParseError, parse } from "../../src/spd/parser";
import { render as renderSvg } from "../../src/spd/svg-renderer";

document.addEventListener("DOMContentLoaded", () => {
  const appVersion = document.getElementById("appVersion") as HTMLSpanElement;
  if (appVersion) {
    appVersion.textContent = version;
  }

  const spdInput = document.getElementById("spdInput") as HTMLTextAreaElement;
  const inputModeSpd = document.getElementById(
    "inputModeSpd",
  ) as HTMLInputElement;
  const inputModeAst = document.getElementById(
    "inputModeAst",
  ) as HTMLInputElement;

  const svgOutput = document.getElementById("svgOutput") as HTMLDivElement;
  const svgViewer = document.getElementById("svgViewer") as HTMLDivElement;
  const astOutput = document.getElementById("astOutput") as HTMLTextAreaElement;
  const errorOutput = document.getElementById("errorOutput") as HTMLDivElement;
  const displayModeSvg = document.getElementById(
    "displayModeSvg",
  ) as HTMLInputElement;
  const displayModeAst = document.getElementById(
    "displayModeAst",
  ) as HTMLInputElement;
  const displayModeAstPretty = document.getElementById(
    "displayModeAstPretty",
  ) as HTMLInputElement;
  const displayModeError = document.getElementById(
    "displayModeError",
  ) as HTMLInputElement;
  const _displayModeErrorLabel = document.getElementById(
    "displayModeErrorLabel",
  ) as HTMLLabelElement;

  const zoomInButton = document.getElementById(
    "zoomInButton",
  ) as HTMLButtonElement;
  const zoomOutButton = document.getElementById(
    "zoomOutButton",
  ) as HTMLButtonElement;
  const resetZoomButton = document.getElementById(
    "resetZoomButton",
  ) as HTMLButtonElement;
  const zoomLevelInput = document.getElementById(
    "zoomLevelInput",
  ) as HTMLInputElement;
  const fullscreenButton = document.getElementById(
    "fullscreenButton",
  ) as HTMLButtonElement;
  const checkerboardButton = document.getElementById(
    "checkerboardButton",
  ) as HTMLButtonElement;
  const checkerboardIcon = document.getElementById(
    "checkerboardIcon",
  ) as HTMLSpanElement;

  let zoomLevel = 1.0;
  let showCheckerboard = true;
  let lastSuccessfulDisplayMode: "svg" | "ast" | "astPretty" = "svg";

  const updateViewerBackground = () => {
    if (showCheckerboard) {
      svgOutput.classList.add("show-checkerboard");
      svgOutput.style.backgroundColor = "";
      checkerboardButton.classList.add("active");
      checkerboardIcon.textContent = "grid_on";
    } else {
      svgOutput.classList.remove("show-checkerboard");
      svgOutput.style.backgroundColor = baseBackgroundColorInput.value;
      checkerboardButton.classList.remove("active");
      checkerboardIcon.textContent = "grid_off";
    }
  };

  checkerboardButton.addEventListener("click", () => {
    showCheckerboard = !showCheckerboard;
    updateViewerBackground();
  });

  const updateZoom = (newLevel?: number) => {
    if (newLevel !== undefined) {
      // 10% から 500% の間にクリップ
      zoomLevel = Math.max(0.1, Math.min(newLevel, 5.0));
    }

    const svgElement = svgOutput.querySelector("svg");
    if (svgElement) {
      svgElement.style.transform = `scale(${zoomLevel})`;
    }
    if (zoomLevelInput) {
      zoomLevelInput.value = Math.round(zoomLevel * 100).toString();
    }
  };

  zoomInButton.addEventListener("click", () => {
    updateZoom(zoomLevel + 0.1);
  });

  zoomOutButton.addEventListener("click", () => {
    updateZoom(zoomLevel - 0.1);
  });

  resetZoomButton.addEventListener("click", () => {
    updateZoom(1.0);
  });

  zoomLevelInput.addEventListener("change", () => {
    const value = parseInt(zoomLevelInput.value, 10);
    if (!Number.isNaN(value)) {
      updateZoom(value / 100);
    } else {
      updateZoom(); // 無効な入力の場合は現在の値にリセット
    }
  });

  zoomLevelInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      zoomLevelInput.blur();
    }
  });

  fullscreenButton.addEventListener("click", () => {
    if (!document.fullscreenElement) {
      svgViewer.requestFullscreen().catch((err) => {
        alert(
          `Error attempting to enable full-screen mode: ${err.message} (${err.name})`,
        );
      });
    } else {
      document.exitFullscreen();
    }
  });

  const fileInput = document.getElementById("fileInput") as HTMLInputElement;
  const downloadButton = document.getElementById(
    "downloadButton",
  ) as HTMLButtonElement;
  const downloadAstButton = document.getElementById(
    "downloadAstButton",
  ) as HTMLButtonElement;
  const downloadSvgButton = document.getElementById(
    "downloadSvgButton",
  ) as HTMLButtonElement;
  const shareButton = document.getElementById(
    "shareButton",
  ) as HTMLButtonElement;

  // Render Options
  const fontSizeInput = document.getElementById(
    "fontSizeInput",
  ) as HTMLInputElement;
  const fontFamilySelect = document.getElementById(
    "fontFamilySelect",
  ) as HTMLSelectElement;
  const fontFamilyCustomInput = document.getElementById(
    "fontFamilyCustomInput",
  ) as HTMLInputElement;
  const customFontCheckbox = document.getElementById(
    "customFontCheckbox",
  ) as HTMLInputElement;
  const baseBackgroundColorInput = document.getElementById(
    "baseBackgroundColorInput",
  ) as HTMLInputElement;
  const transparentBackgroundCheckbox = document.getElementById(
    "transparentBackgroundCheckbox",
  ) as HTMLInputElement;
  const backgroundColorInput = document.getElementById(
    "backgroundColorInput",
  ) as HTMLInputElement;
  const transparentNodeBackgroundCheckbox = document.getElementById(
    "transparentNodeBackgroundCheckbox",
  ) as HTMLInputElement;
  const textColorInput = document.getElementById(
    "textColorInput",
  ) as HTMLInputElement;
  const listRenderTypeTerminalOffset = document.getElementById(
    "listRenderTypeTerminalOffset",
  ) as HTMLInputElement;
  const applyOptionsButton = document.getElementById(
    "applyOptionsButton",
  ) as HTMLButtonElement;

  const convertAndRender = () => {
    const spdText = spdInput.value;
    const _isSvgMode = displayModeSvg.checked;
    const isAstPrettyMode = displayModeAstPretty.checked;

    // 現在の選択がErrorでない場合は、最後の成功したモードを更新
    if (!displayModeError.checked) {
      if (displayModeSvg.checked) lastSuccessfulDisplayMode = "svg";
      else if (displayModeAst.checked) lastSuccessfulDisplayMode = "ast";
      else if (displayModeAstPretty.checked)
        lastSuccessfulDisplayMode = "astPretty";
    }

    try {
      const ast = inputModeAst.checked
        ? deserializeAST(spdText)
        : parse(spdText);

      if (!ast) {
        throw new Error("Could not obtain AST.");
      }

      // 成功時はラジオボタンを有効化し、Error表示を隠す
      displayModeSvg.disabled = false;
      displayModeAst.disabled = false;
      displayModeAstPretty.disabled = false;
      displayModeError.disabled = true;
      errorOutput.style.display = "none";

      // 最後の成功したモードに戻す（もしErrorモードだった場合）
      if (displayModeError.checked) {
        if (lastSuccessfulDisplayMode === "svg") displayModeSvg.checked = true;
        else if (lastSuccessfulDisplayMode === "ast")
          displayModeAst.checked = true;
        else if (lastSuccessfulDisplayMode === "astPretty")
          displayModeAstPretty.checked = true;
      }

      if (displayModeSvg.checked) {
        svgViewer.style.display = "flex";
        astOutput.style.display = "none";
        downloadSvgButton.style.display = "inline-flex";
        downloadAstButton.style.display = "none";

        const options = {
          fontSize: parseInt(fontSizeInput.value, 10),
          fontFamily: customFontCheckbox.checked
            ? fontFamilyCustomInput.value
            : fontFamilySelect.value,
          baseBackgroundColor: transparentBackgroundCheckbox.checked
            ? null
            : baseBackgroundColorInput.value,
          backgroundColor: transparentNodeBackgroundCheckbox.checked
            ? null
            : backgroundColorInput.value,
          textColor: textColorInput.value,
          listRenderType: listRenderTypeTerminalOffset.checked
            ? "TerminalOffset"
            : "Original",
        };
        const svgString = renderSvg(ast, options);

        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgString, "image/svg+xml");
        const svgElement = svgDoc.documentElement;

        svgOutput.textContent = "";
        if (svgElement && svgElement.tagName === "svg") {
          svgOutput.appendChild(svgElement);
          updateZoom();
        } else {
          // Fallback for cases where parsing might fail (though renderSvg should return valid SVG)
          svgOutput.innerHTML = svgString;
          updateZoom();
        }
      } else {
        svgViewer.style.display = "none";
        astOutput.style.display = "block";
        astOutput.value = isAstPrettyMode
          ? serializeAST(ast, 2)
          : serializeAST(ast);
        downloadSvgButton.style.display = "none";
        downloadAstButton.style.display = "inline-flex";
      }
    } catch (error) {
      // エラー時はラジオボタンを無効化し、Error表示に切り替える
      displayModeSvg.disabled = true;
      displayModeAst.disabled = true;
      displayModeAstPretty.disabled = true;
      displayModeError.disabled = false;
      displayModeError.checked = true;

      const errorDiv = document.createElement("div");
      errorDiv.classList.add("error-message");
      svgOutput.textContent = "";
      astOutput.value = "";

      svgViewer.style.display = "none";
      astOutput.style.display = "none";
      errorOutput.style.display = "block";
      errorOutput.textContent = "";

      downloadSvgButton.style.display = "none";
      downloadAstButton.style.display = "none";

      if (error instanceof ParseError) {
        const msgPara = document.createElement("div");
        if (error.lineNo !== undefined && error.lineStr !== undefined) {
          msgPara.textContent = `Error at line ${error.lineNo}: ${error.message}`;
          errorDiv.appendChild(msgPara);

          const lineCode = document.createElement("code");
          lineCode.classList.add("error-line");
          lineCode.textContent = error.lineStr;
          errorDiv.appendChild(lineCode);
        } else {
          msgPara.textContent = `Error: ${error.message}`;
          errorDiv.appendChild(msgPara);
        }
        console.error("SPD conversion error:", error);
      } else if (error instanceof Error) {
        const msgPara = document.createElement("div");
        msgPara.textContent = `Error: ${error.message}`;
        errorDiv.appendChild(msgPara);
        console.error("SPD conversion error:", error);
      } else {
        errorDiv.textContent = "An unknown error occurred";
        console.error("An unknown error occurred:", error);
      }

      errorOutput.appendChild(errorDiv);
    }
  };

  customFontCheckbox.addEventListener("change", () => {
    if (customFontCheckbox.checked) {
      fontFamilySelect.style.display = "none";
      fontFamilyCustomInput.style.display = "inline-block";
    } else {
      fontFamilySelect.style.display = "inline-block";
      fontFamilyCustomInput.style.display = "none";
    }
    convertAndRender();
  });

  transparentBackgroundCheckbox.addEventListener("change", () => {
    baseBackgroundColorInput.disabled = transparentBackgroundCheckbox.checked;
  });

  transparentNodeBackgroundCheckbox.addEventListener("change", () => {
    backgroundColorInput.disabled = transparentNodeBackgroundCheckbox.checked;
  });

  spdInput.addEventListener("input", convertAndRender);
  inputModeSpd.addEventListener("change", convertAndRender);
  inputModeAst.addEventListener("change", convertAndRender);
  displayModeSvg.addEventListener("change", convertAndRender);
  displayModeAst.addEventListener("change", convertAndRender);
  displayModeAstPretty.addEventListener("change", convertAndRender);
  applyOptionsButton.addEventListener("click", convertAndRender);

  spdInput.addEventListener("keydown", (event) => {
    if (event.key === "Tab") {
      event.preventDefault(); // デフォルトのTabキーの動作（フォーカス移動）をキャンセル
      const start = spdInput.selectionStart;
      const end = spdInput.selectionEnd;

      // タブ文字を挿入
      spdInput.value =
        spdInput.value.substring(0, start) +
        "	" +
        spdInput.value.substring(end);

      // カーソル位置をタブの直後に設定
      spdInput.selectionStart = spdInput.selectionEnd = start + 1;

      convertAndRender(); // タブ挿入後も変換を実行
    }
  });

  fileInput.addEventListener("change", (event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        spdInput.value = e.target?.result as string;
        // .jsonファイルの場合は自動的に「ASTとしてインポート」をオンにする
        if (file.name.endsWith(".json")) {
          inputModeAst.checked = true;
        } else if (file.name.endsWith(".spd")) {
          inputModeSpd.checked = true;
        }
        convertAndRender(); // ファイル読み込み後も変換を実行
      };
      reader.readAsText(file);
    }
  });

  const getFileName = (message: string, defaultName: string): string | null => {
    try {
      return prompt(message, defaultName);
    } catch (error) {
      console.warn("prompt() is not supported, using default filename.", error);
      return defaultName;
    }
  };

  downloadButton.addEventListener("click", () => {
    const spdText = spdInput.value;
    const isAst = inputModeAst.checked;
    const defaultFileName = isAst ? "input_ast.json" : "edited_spd.spd";
    const fileNameInput = getFileName(
      "ダウンロードするファイル名を入力してください:",
      defaultFileName,
    );

    if (fileNameInput === null) {
      // ユーザーがキャンセルした場合
      return;
    }

    let fileName = fileNameInput;
    // 拡張子がない場合は適切に追加
    if (isAst) {
      if (!fileName.endsWith(".json")) {
        fileName += ".json";
      }
    } else {
      if (!fileName.endsWith(".spd")) {
        fileName += ".spd";
      }
    }

    const mimeType = isAst ? "application/json" : "text/plain";
    const blob = new Blob([spdText], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName; // ユーザーが入力したファイル名を使用
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  });

  downloadAstButton.addEventListener("click", () => {
    const spdText = spdInput.value;
    try {
      const ast = inputModeAst.checked
        ? deserializeAST(spdText)
        : parse(spdText);

      if (!ast) {
        alert("ASTを取得できませんでした。内容を確認してください。");
        return;
      }

      const isAstPrettyMode = displayModeAstPretty.checked;
      const astJson = isAstPrettyMode
        ? serializeAST(ast, 2)
        : serializeAST(ast);

      const fileNameInput = getFileName(
        "ダウンロードするASTファイル名を入力してください:",
        "ast.json",
      );

      if (fileNameInput === null) {
        return;
      }

      let fileName = fileNameInput;
      if (!fileName.endsWith(".json")) {
        fileName += ".json";
      }

      const blob = new Blob([astJson], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(
        `ASTの生成に失敗しました: ${error instanceof Error ? error.message : error}`,
      );
    }
  });

  downloadSvgButton.addEventListener("click", () => {
    const svgString = svgOutput.innerHTML;
    if (!svgString) {
      alert("SVGが生成されていません。");
      return;
    }

    const fileNameInput = getFileName(
      "ダウンロードするSVGファイル名を入力してください:",
      "output.svg",
    );

    if (fileNameInput === null) {
      // ユーザーがキャンセルした場合
      return;
    }

    let fileName = fileNameInput;
    // 拡張子がない場合は.svgを追加
    if (!fileName.endsWith(".svg")) {
      fileName += ".svg";
    }

    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  });

  shareButton.addEventListener("click", () => {
    const spdText = spdInput.value;
    if (!spdText) {
      alert("SPDが入力されていません。");
      return;
    }

    try {
      // UTF-8文字列をBase64にエンコード
      const bytes = new TextEncoder().encode(spdText);
      const binString = Array.from(bytes, (byte) =>
        String.fromCodePoint(byte),
      ).join("");
      const base64 = btoa(binString);

      const url = new URL(window.location.href);
      url.searchParams.set("spd", base64);
      if (inputModeAst.checked) {
        url.searchParams.set("ast", "1");
      }

      navigator.clipboard
        .writeText(url.toString())
        .then(() => {
          alert("共有用URLをクリップボードにコピーしました。");
        })
        .catch((err) => {
          console.error("Failed to copy URL:", err);
          alert("URLのコピーに失敗しました。");
        });
    } catch (error) {
      console.error("Failed to encode SPD:", error);
      alert("URLの生成に失敗しました。");
    }
  });

  // 初期表示用のデータ取得
  const urlParams = new URLSearchParams(window.location.search);
  const spdParam = urlParams.get("spd");
  const astParam = urlParams.get("ast");

  if (spdParam) {
    try {
      // Base64をデコード（UTF-8対応）
      const binString = atob(spdParam);
      const bytes = Uint8Array.from(binString, (m) => m.codePointAt(0) ?? 0);
      spdInput.value = new TextDecoder().decode(bytes);

      if (astParam === "1") {
        inputModeAst.checked = true;
      }
    } catch (error) {
      console.error("Failed to decode SPD from URL parameter:", error);
      // デコードに失敗した場合はデフォルトを表示
      spdInput.value = getDefaultSpd();
    }
  } else {
    spdInput.value = getDefaultSpd();
  }

  baseBackgroundColorInput.addEventListener("input", () => {
    updateViewerBackground();
  });

  convertAndRender(); // ページロード時に一度変換を実行
  updateViewerBackground(); // 初期背景設定
});

function getDefaultSpd(): string {
  return `:terminal 開始
命令
:comment コメント文
:call 関数呼び出し
	中身
:if 条件式
	真の場合
:else
	偽の場合(:else以下は省略可能)
:switch 条件
:case ケース1
	ケース1の中身
:case ケース2
	ケース2の中身
:case ...
	ケース文は必要に応じていくつでも追加できます
:while 繰り返し条件（先判定）
	中身
:dowhile 繰り返し条件（後判定）
	中身
:terminal 終了`;
}
